import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../../../utils/api';

const FiltersBar = ({ filters, onChange, onApply }) => {
  const [hierarchyRows, setHierarchyRows] = useState([]);

  const [uiFilters, setUiFilters] = useState({
    site: '',
    location: '',
    sublocation: ''
  });

  const [localFilters, setLocalFilters] = useState(filters);

  // Sync from parent
  useEffect(() => {
    setLocalFilters(filters);
    setUiFilters({
      site: filters?.site_id || '',
      location: filters?.location_id || '',
      sublocation: filters?.sublocation_id || ''
    });
  }, [filters]);

  // Fetch hierarchy rows from the new reports router
  useEffect(() => {
    const fetchHierarchy = async () => {
      try {
        const res = await apiFetch('/updates-reports/hierarchy');
        const data = await res.json();
        setHierarchyRows(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching reports hierarchy:', err);
        setHierarchyRows([]);
      }
    };
    fetchHierarchy();
  }, []);

  const sites = useMemo(() => {
    const map = new Map();
    for (const row of hierarchyRows) {
      if (row.site_id == null) continue;
      if (!map.has(row.site_id)) {
        map.set(row.site_id, row.site_name || `Site ${row.site_id}`);
      }
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id: String(id), name }));
  }, [hierarchyRows]);

  const locations = useMemo(() => {
    const map = new Map();
    for (const row of hierarchyRows) {
      if (row.location_id == null) continue;
      if (uiFilters.site && String(row.site_id) !== String(uiFilters.site)) continue;
      if (!map.has(row.location_id)) {
        map.set(row.location_id, row.location_name || `Location ${row.location_id}`);
      }
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id: String(id), name }));
  }, [hierarchyRows, uiFilters.site]);

  const sublocations = useMemo(() => {
    const map = new Map();
    for (const row of hierarchyRows) {
      if (row.sublocation_id == null) continue;
      if (uiFilters.site && String(row.site_id) !== String(uiFilters.site)) continue;
      if (uiFilters.location && String(row.location_id) !== String(uiFilters.location)) continue;
      if (!map.has(row.sublocation_id)) {
        map.set(row.sublocation_id, row.sublocation_name || `Sublocation ${row.sublocation_id}`);
      }
    }
    return Array.from(map.entries()).map(([id, name]) => ({ id: String(id), name }));
  }, [hierarchyRows, uiFilters.site, uiFilters.location]);

  const stations = useMemo(() => {
    const map = new Map();
    for (const row of hierarchyRows) {
      if (row.station_id == null) continue;
      if (uiFilters.site && String(row.site_id) !== String(uiFilters.site)) continue;
      if (uiFilters.location && String(row.location_id) !== String(uiFilters.location)) continue;
      if (uiFilters.sublocation && String(row.sublocation_id) !== String(uiFilters.sublocation)) continue;
      if (!map.has(row.station_id)) {
        map.set(row.station_id, row.station_name || '');
      }
    }
    return Array.from(map.entries()).map(([station_id, station_name]) => ({
      station_id: String(station_id),
      station_name
    }));
  }, [hierarchyRows, uiFilters.site, uiFilters.location, uiFilters.sublocation]);

  // If selected station is no longer valid after cascade changes, clear it.
  useEffect(() => {
    const selectedStation = String(localFilters.station || '');
    if (!selectedStation) return;
    const exists = stations.some((st) => String(st.station_id) === selectedStation);
    if (!exists) {
      const updated = { ...localFilters, station: '' };
      setLocalFilters(updated);
      onChange(updated);
    }
  }, [stations, localFilters, onChange]);

  // Handle UI filter changes
  const handleUiChange = (e) => {
    const { name, value } = e.target;

    if (name === 'site') {
      const nextUi = { site: value, location: '', sublocation: '' };
      const updated = {
        ...localFilters,
        site_id: value || '',
        location_id: '',
        sublocation_id: '',
        station: ''
      };
      setUiFilters(nextUi);
      setLocalFilters(updated);
      onChange(updated);
    }
    if (name === 'location') {
      const nextUi = { ...uiFilters, location: value, sublocation: '' };
      const updated = {
        ...localFilters,
        site_id: nextUi.site || '',
        location_id: value || '',
        sublocation_id: '',
        station: ''
      };
      setUiFilters(nextUi);
      setLocalFilters(updated);
      onChange(updated);
    }
    if (name === 'sublocation') {
      const nextUi = { ...uiFilters, sublocation: value };
      const updated = {
        ...localFilters,
        site_id: nextUi.site || '',
        location_id: nextUi.location || '',
        sublocation_id: value || '',
        station: ''
      };
      setUiFilters(nextUi);
      setLocalFilters(updated);
      onChange(updated);
    }
  };

  // Handle actual filters (station + dates)
  const handleChange = (e) => {
    const updated = { ...localFilters, [e.target.name]: e.target.value };
    setLocalFilters(updated);
    onChange(updated);
  };

  const handleApplyFilters = () => {
    const appliedFilters = {
      station: localFilters.station || '',
      startDate: localFilters.startDate || '',
      endDate: localFilters.endDate || '',
      daypart: localFilters.daypart || '',
      site_id: uiFilters.site || '',
      location_id: uiFilters.location || '',
      sublocation_id: uiFilters.sublocation || ''
    };

    onApply(appliedFilters);
  };

  const handleClearFilters = () => {
    const resetFilters = {
      station: '',
      startDate: '',
      endDate: '',
      daypart: '',
      site_id: '',
      location_id: '',
      sublocation_id: ''
    };

    setUiFilters({
      site: '',
      location: '',
      sublocation: ''
    });
    setLocalFilters(resetFilters);
    onChange(resetFilters);
    onApply(resetFilters);
  };

  return (
    <div className="flex flex-row gap-2.5 mt-4 items-center">
      {/* Site Dropdown */}
      <select
        name="site"
        value={uiFilters.site}
        onChange={handleUiChange}
        className="border px-3 py-2 rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
      >
        <option value="">All Sites</option>
        {sites.map((s) => (
          <option key={s.id} value={s.id}>{s.name}</option>
        ))}
      </select>

      {/* Location Dropdown */}
      <select
        name="location"
        value={uiFilters.location}
        onChange={handleUiChange}
        className="border px-3 py-2 rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
        disabled={!uiFilters.site}
      >
        <option value="">All Locations</option>
        {locations.map((l) => (
          <option key={l.id} value={l.id}>{l.name}</option>
        ))}
      </select>

      {/* Sublocation Dropdown */}
      <select
        name="sublocation"
        value={uiFilters.sublocation}
        onChange={handleUiChange}
        className="border px-3 py-2 rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
        disabled={!uiFilters.location}
      >
        <option value="">All Sublocations</option>
        {sublocations.map((sl) => (
          <option key={sl.id} value={sl.id}>{sl.name}</option>
        ))}
      </select>

      {/* Station Dropdown */}
      <select
        name="station"
        value={localFilters.station || ''}
        onChange={handleChange}
        className="border px-3 py-2 rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
        disabled={!stations.length}
      >
        <option value="">All Stations</option>
        {stations.map((st) => (
          <option key={st.station_id} value={st.station_id}>
            {st.station_name ? `${st.station_name} (${st.station_id})` : st.station_id}
          </option>
        ))}
      </select>

      {/* Meal Period Dropdown */}
      <select
        name="daypart"
        value={localFilters.daypart || ''}
        onChange={handleChange}
        className="border px-3 py-2 rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
      >
        <option value="">All Meal Periods</option>
        <option value="Breakfast">Breakfast</option>
        <option value="Lunch">Lunch</option>
        <option value="Dinner">Dinner</option>
        <option value="Late Night">Late Night</option>
      </select>

      {/* Date Pickers */}
      <input
        type="date"
        name="startDate"
        value={localFilters.startDate || ''}
        onChange={handleChange}
        className="border px-3 py-2 rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
      />
      <input
        type="date"
        name="endDate"
        value={localFilters.endDate || ''}
        onChange={handleChange}
        className="border px-3 py-2 rounded bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600"
      />

      <button
        type="button"
        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        onClick={handleApplyFilters}
      >
        APPLY
      </button>

      <button
        type="button"
        className="border border-gray-300 px-4 py-2 rounded text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
        onClick={handleClearFilters}
      >
        RESET
      </button>
    </div>
  );
};

export default FiltersBar;
